import { useState, useEffect, useRef, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import api, { formatDate } from "../lib/api";
import { toast } from "sonner";
import { SUPPORT_EMAIL } from "../lib/brand";
import ComplianceReadiness from "../components/ComplianceReadiness";
import WhyWeAskDialog from "../components/WhyWeAskDialog";
import OnboardingProgressBar from "../components/OnboardingProgressBar";
import SecurityAssurance from "../components/SecurityAssurance";
import {
  Upload,
  FileCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Download,
  ScrollText
} from "lucide-react";

// Member-facing wording, keyed by the server catalog's `type`
// (CAPMARKETS-126). The SET of types is the server's; only the phrasing is
// ours, so this map can never introduce a type the reviewer cannot display.
const DOC_TYPE_HINTS = {
  id_document: "Government ID (Passport/Driver's License)",
  proof_of_address: "Proof of Address (Utility Bill/Bank Statement)",
};

// Used only when GET /kyc/catalog is unreachable — better than leaving the
// member unable to upload. The upload endpoint validates against the real
// catalog regardless, so a stale fallback cannot store a bad type.
// `required` is load-bearing since CAPMARKETS-247: the slot rows render a
// required/optional badge from it. Omit it here and a member who loads the page
// while /kyc/catalog is unreachable is told the two documents that gate
// `users.kyc_status` are optional. Keep these flags in step with DOC_CATALOG in
// `services/kyc_catalog.py`.
const DOC_TYPE_FALLBACK = [
  { type: "id_document", label: "Government ID", required: true },
  { type: "proof_of_address", label: "Proof of Address", required: true },
  { type: "bank_statement", label: "Bank Statement", required: false },
  { type: "selfie", label: "Selfie with ID", required: false },
];

// STARSEED-CAPMARKETS-251 — one row per `users.kyc_status` value.
//
// This replaces four independent ternary chains keyed on the same field (card
// tint, icon, heading + subtitle, badge), each carrying its own `:` fallback, so
// a value nobody thought about landed in whatever the last branch happened to
// say, in four places, silently.
//
// The vocabulary, from every write site found by `git grep kyc_status` across
// the API and admin UI (the set is open — see the fallback below):
//   pending    `models/users.py:51` — the default EVERY member carries — and
//              `routes/user_invites.py:565` for a non-privileged invitee
//   in_review  `routes/onboarding.py:190-191,219-222` — `POST /onboarding/sumsub/start`,
//              written when the SDK token is minted (or the placeholder
//              session is queued), BEFORE the member submits anything — and
//              the SumSub `applicantPending` webhook, `onboarding.py:305-306`,
//              after submission. One value, two situations; see its row.
//   approved   `routes/kyc.py:308,402` (required documents approved, and its
//              repair path), `routes/onboarding.py:302,538` (SumSub GREEN, admin
//              mark), `server.py:685`
//   verified   `routes/user_invites.py:565`, privileged invitees only
//   resubmission_requested   SumSub non-final RED, `routes/onboarding.py:304`
//   rejected   SumSub FINAL RED (`onboarding.py:304`) and the admin create-user
//              form (`UsersPage.jsx`, persisted by `routes/users.py:184`)
//   on_hold    SumSub `applicantOnHold`, `routes/onboarding.py:308`
// `PUT /users/{id}/kyc-status` (`routes/users.py:587`) stores any string, so
// this list is what the code writes today, not a closed enum.
// `not_started`, `failed` and `complete` look like members of this set from a
// grep and are not: those live on `verification_requests` and on a FinBPM
// delivery receipt.
//
// **No line here asserts anything about the member's documents**, because this
// field does not carry that. `pending` is the default and is equally true of a
// member who has uploaded nothing and one whose two required documents are
// sitting in the review queue — which is exactly what the old copy got wrong
// when it told a member with an empty checklist that their documents were under
// review. The surface that knows the difference is the checklist above, which
// renders the server's `_kyc_light`; the copy points there rather than guessing.
//
// The `pending` line does not name documents AT ALL, which is stricter than it
// needs to be and deliberately so (Copilot, PR #2586): a rule the test can check
// exactly beats a rule that needs adjudicating.
//
// `verified` makes NO identity claim and is not styled as success. It is written
// at invite time with no document or SumSub review, every server gate
// (`onboarding_checklist.py:89`, `command_center_engine.py:170`,
// `qualification_service.py:30`) treats the member as not KYC-complete, and what
// the value is supposed to mean is CAPMARKETS-252's open question. This copy
// stays true however 252 is decided; it must not answer it.
//
// `rejected` makes no promise of a way back, because none is recorded: it names
// the support address (`lib/brand.js`) instead. `resubmission_requested` is the
// one value that asks the member to act, and names where — the Verification &
// Accreditation page re-offers the SumSub start for any status other than
// `approved` / `in_review` (`KYCVerificationPage.jsx`, `onboarding.py:190`).
//
// `in_review` says only that the check has been STARTED — heading included,
// which reads "In progress" rather than "In review" — and points at the page
// where the check is started or continued. That page has its own `in_review`
// problem (an "In Review" badge and a disabled Continue KYC button), filed as
// CAPMARKETS-294 and not changed here.
//
// It must not say the check is with the reviewer: the value is written when the
// Start button mints the SumSub token, so a member who opened the widget and
// closed it without submitting holds it too, and telling that member the
// reviewer has it describes a review of nothing — the banner gives them no
// reason to go back (/code-review re-review, PR #2586). The earlier line, "Your
// identity check is with the reviewer", was true only for the post-submission
// half. The in-progress tone stays: a check has been started and not concluded,
// which is true whichever side is holding it.
export const KYC_BANNER = {
  approved:  { label: "Approved",  tone: "ok",      line: "Your identity has been verified" },
  verified:  { label: "Verified by invitation", tone: "todo", line: "See the checklist above for where your verification stands" },
  in_review: { label: "In progress", tone: "waiting", line: "Your identity check has been started — see Verification & Accreditation for where it stands" },
  on_hold:   { label: "On hold",   tone: "waiting", line: "Your identity check is on hold with the reviewer" },
  resubmission_requested: {
    label: "Resubmission requested", tone: "action",
    line: "The reviewer asked for your identity check to be resubmitted — restart it from Verification & Accreditation",
  },
  rejected:  { label: "Not approved", tone: "stopped", line: `Your identity check was not approved — for help, contact ${SUPPORT_EMAIL}` },
  pending:   { label: "Pending",   tone: "todo",    line: "Not verified yet — see the checklist above" },
};

// An unrecognised status says NOTHING rather than something false, and that is
// the whole property the old code lacked: its fallback branch told everyone
// outside {approved, pending} to "Please upload required documents", so a
// `verified` privileged invitee was asked to upload documents they never needed
// and an `in_review` member — who may have started the SumSub check or already
// submitted it — was told to upload documents. A status added server-side and
// not here now renders the raw value and no claim — visibly incomplete instead
// of confidently wrong.
export const KYC_BANNER_FALLBACK = { label: null, tone: "todo", line: null };

// Exported so `kycStatusBanner.render.test.jsx` can assert that every row's
// `tone` resolves. Without that guard the mistake is invisible: a row added with
// a tone nobody added here passes the ENTIRE suite (no case renders it) and then
// throws `Cannot read properties of undefined (reading 'card')` in production —
// crashing the whole page, for exactly the members holding that status. Verified
// by inducing it. The runtime fallback below keeps it from being fatal; this
// export is what makes it fail in CI instead.
export const KYC_TONE = {
  ok:      { Icon: CheckCircle2, card: "bg-emerald/10",    icon: "text-emerald",    badge: "bg-emerald/20 text-emerald" },
  waiting: { Icon: Clock,        card: "bg-orange-500/10", icon: "text-orange-400", badge: "bg-orange-500/20 text-orange-400" },
  action:  { Icon: Upload,       card: "bg-orange-500/10", icon: "text-orange-400", badge: "bg-orange-500/20 text-orange-400" },
  stopped: { Icon: XCircle,      card: "bg-red-500/10",    icon: "text-red-400",    badge: "bg-red-500/20 text-red-400" },
  todo:    { Icon: FileCheck,    card: "bg-surface",       icon: "text-gray-400",   badge: "bg-gray-500/20 text-gray-400" },
};

const KYCPage = () => {
  const { user, isAdmin, refreshUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [reviewDialog, setReviewDialog] = useState({ open: false, doc: null });
  const [reviewNotes, setReviewNotes] = useState("");
  const [downloadingBylaws, setDownloadingBylaws] = useState(false);
  // STARSEED-CAPMARKETS-250 — bumped by `refreshAfterMutation`; see below.
  const [complianceToken, setComplianceToken] = useState(0);
  const fileInputRef = useRef(null);
  const uploadCardRef = useRef(null);
  // STARSEED-CAPMARKETS-247: the slot the file picker was opened FOR.
  //
  // This used to be `selectedType` state, shared by a dropdown and a dropzone
  // and never reset after an upload — so a member who had just filed their
  // driver's license found the form still set to Government ID and their
  // utility bill became a second one. A ref, written by the same click that
  // opens the picker, has no value to go stale between uploads and no commit
  // to race: there is no "current type", only the type of the button pressed.
  const pendingTypeRef = useRef(null);

  // Club Documents (STARSEED-CAPMARKETS-083): the bylaws download is gated
  // server-side on approved membership; mirror the same gate here so the
  // card only renders for members who will actually get a 200. Both flags
  // are required — `membership_active` alone is set at account creation on
  // the deferred-payment path (CAPMARKETS-067) and is NOT an approval signal.
  const canDownloadBylaws =
    isAdmin || (Boolean(user?.membership_active) && Boolean(user?.registration_paid));

  const downloadBylaws = async () => {
    setDownloadingBylaws(true);
    try {
      const r = await api.get("/club-documents/bylaws", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "echosense-capital-club-bylaws-attorney-review-draft.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the bylaws — please try again or contact the team.");
    } finally {
      setDownloadingBylaws(false);
    }
  };

  // Open the OS file picker FOR A NAMED SLOT. Every caller passes the slot
  // explicitly — the per-slot buttons in this page's upload card and in
  // ComplianceReadiness. There is deliberately no "use whatever is currently
  // selected" path, because that is the bug this ticket removes.
  //
  // The click is issued synchronously inside the originating user gesture: the
  // ref is already set, so nothing needs to wait for React to commit, and the
  // picker keeps the gesture context browsers require to open it.
  const triggerUpload = (docType) => {
    if (!docType) return;
    pendingTypeRef.current = docType;
    if (uploadCardRef.current) {
      uploadCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (fileInputRef.current) {
      // Reset so re-uploading the same filename re-fires onChange
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const myDocs = await api.get(`/kyc/user/${user?.id}`);
      setDocuments(myDocs.data);

      if (isAdmin) {
        const pending = await api.get("/kyc/pending");
        setPendingDocs(pending.data);
      }
    } catch (error) {
      toast.error("Failed to load KYC documents");
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  // STARSEED-CAPMARKETS-250 — refresh BOTH reads after a change to the signed-in
  // member's own documents.
  //
  // The page shows the same underlying fact twice, read from two endpoints: the
  // slot rows come from `/kyc/user/{id}` (this component's `documents`) and the
  // checklist above them comes from `/compliance/me` (ComplianceReadiness's own
  // state). Before this, an upload refreshed only the first, so the checklist
  // kept saying "Not uploaded" about a document the row beneath it had already
  // listed by name. One function does both, so an upload cannot refresh the
  // rows without the checklist.
  //
  // `handleReview` deliberately does NOT call this. The checklist is the
  // REVIEWER's own `/compliance/me`, and the review endpoint refuses a review of
  // one's own document (four-eyes, STARSEED-CAPMARKETS-135: 409 in
  // `routes/kyc.py`, superuser not exempt). So a review that succeeds is always
  // of another member's document and cannot have changed this checklist: the
  // refetch would re-read an identical payload, and a failed one would toast
  // "could not be refreshed" right after "Document approved".
  const refreshAfterMutation = useCallback(() => {
    fetchDocuments();
    setComplianceToken((n) => n + 1);
  }, [fetchDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // The server catalog is the single source of truth for document types
  // (CAPMARKETS-126). If it is unreachable we fall back to the built-in list
  // rather than leaving the member with no way to upload; the upload endpoint
  // validates against the same catalog either way.
  useEffect(() => {
    let cancelled = false;
    api.get("/kyc/catalog")
      .then((r) => { if (!cancelled) setCatalog(r.data?.documents || []); })
      .catch(() => { /* fall back to DOC_TYPE_FALLBACK */ });
    return () => { cancelled = true; };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Set by triggerUpload in the same gesture that opened this picker. Absent
    // it we do not guess a slot — guessing is what filed utility bills as
    // Government ID.
    const docType = pendingTypeRef.current;
    if (!docType) {
      toast.error("Please choose which document you are uploading.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await api.post(`/kyc/upload?document_type=${docType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded successfully");
      refreshAfterMutation();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      pendingTypeRef.current = null;
    }
  };

  const handleReview = async (docId, status) => {
    try {
      await api.put(`/kyc/${docId}/review?status=${status}&notes=${reviewNotes}`);
      toast.success(`Document ${status}`);
      setReviewDialog({ open: false, doc: null });
      setReviewNotes("");
      // Rows and the pending queue only: a review never changes the reviewer's
      // own checklist (four-eyes). See the note on `refreshAfterMutation`.
      fetchDocuments();
      refreshUser();
    } catch (error) {
      toast.error("Failed to review document");
    }
  };

  // CAPMARKETS-126: the SET of document types comes from the server catalog —
  // the same list the Investor File panel loops — so the dropdown can no
  // longer offer a type the reviewer surfaces cannot display. These hints are
  // member-facing wording only, keyed by the server's type.
  const documentTypes = (catalog.length ? catalog : DOC_TYPE_FALLBACK).map((d) => ({
    value: d.type,
    label: DOC_TYPE_HINTS[d.type] || d.label,
    required: d.required,
  }));

  // STARSEED-CAPMARKETS-247 — one row per catalog slot, carrying the document
  // already in it. Showing the current file name is the whole point: a member
  // about to drop `utility-bill.pdf` onto a row that reads
  // `drivers-license.jpg` is answering a different question than one facing an
  // unlabelled dropzone.
  //
  // Newest wins per slot, matching the server's `latest_by_type` — the
  // projection every other surface renders, so the member and the reviewer
  // describe the same slot the same way.
  //
  // The order is established here, NOT assumed: `/kyc/user/{id}`
  // (`routes/kyc.py`) has no `.sort()`, so documents arrive oldest-first. An
  // earlier draft of this took `documents.find(...)` on the strength of a
  // newest-first comment copied from `_kyc_docs_for_user` in `compliance.py`,
  // which is a different endpoint that does sort. On a doubled slot that
  // showed the OLDER file — for the reported member, their correct driver's
  // license — hiding the very mistake this row exists to reveal.
  const slots = documentTypes.map((t) => {
    const current = documents
      .filter((d) => d.document_type === t.value)
      .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))[0];
    return {
      ...t,
      current,
      state: current ? current.status : "missing",
    };
  });

  // STARSEED-CAPMARKETS-251 — resolved once, read by every part of the banner,
  // so the tint, the icon, the heading, the line and the badge cannot disagree
  // about the same member the way four separate ternaries could.
  const kycStatus = user?.kyc_status;
  const kycBanner = KYC_BANNER[kycStatus] || KYC_BANNER_FALLBACK;
  // `|| KYC_TONE.todo` is not defensive noise — see the note on KYC_TONE. An
  // unresolved tone here is a TypeError during render, which takes the page
  // down rather than the banner; neutral styling with the correct words is a
  // strictly better failure than a white screen.
  const kycTone = KYC_TONE[kycBanner.tone] || KYC_TONE.todo;

  return (
    <DashboardLayout 
      title="Documents"
      subtitle="Upload and manage identity verification documents"
    >
      {/* Step indicator — Identity Verification is Step 2 of 4 */}
      <div className="mb-6">
        <OnboardingProgressBar currentStep={2} />
      </div>

      {/* Cybersecurity assurance — soft-tone, non-boastful */}
      <div className="mb-6">
        <SecurityAssurance variant="kyc" />
      </div>

      {/* New unified Compliance Readiness widget — shows traffic-light progress + checklist */}
      <div className="mb-6">
        <ComplianceReadiness onUploadClick={triggerUpload} refreshToken={complianceToken} />
      </div>

      {/* Page-level hidden file input — driven by ComplianceReadiness buttons,
          the top-right CTA, AND the dotted-box label below. One source of truth. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={handleUpload}
        disabled={uploading}
        data-testid="kyc-file-input"
      />

      {/* "Why do we need this info?" — drives understanding of the value behind the data ask */}
      <div className="mb-4 flex justify-end">
        <WhyWeAskDialog testId="why-we-ask-kyc-page" />
      </div>

      {/* Status Banner — STARSEED-CAPMARKETS-251: every part reads one row. */}
      <Card className={`mb-6 border-white/5 ${kycTone.card}`} data-testid="kyc-status-banner">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <kycTone.Icon className={kycTone.icon} size={24} />
            <div>
              <p className="text-white font-medium">
                KYC Status: {kycBanner.label || kycStatus || "unknown"}
              </p>
              {/* Absent, not empty, when the status is one this page does not
                  recognise — there is nothing truthful to say about it. */}
              {kycBanner.line && (
                <p className="text-gray-400 text-sm" data-testid="kyc-status-line">{kycBanner.line}</p>
              )}
            </div>
          </div>
          <Badge className={kycTone.badge} data-testid="kyc-status-badge">
            {kycStatus}
          </Badge>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Section */}
        <Card ref={uploadCardRef} className="bg-surface border-white/5" data-testid="kyc-upload-card">
          <CardHeader>
            <CardTitle className="text-white font-serif">Upload Documents</CardTitle>
            <CardDescription>Each document has its own place — pick the one you are uploading</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {slots.map((slot) => {
              const filled = Boolean(slot.current);
              return (
                // STARSEED-CAPMARKETS-248 — stacked, not side-by-side.
                //
                // This card sits in a 1/3 grid column (~300px). Laid out as a
                // single `justify-between` row the label collapsed to 47px and
                // wrapped over four lines, and the file name truncated to
                // "utility-…" — measured in the browser, invisible to the
                // render tests, which assert text content and not legibility.
                //
                // The file name is the entire point of the slot row: a member
                // about to drop `utility-bill.pdf` onto a row reading
                // `drivers-license.jpg` is answering a different question. A
                // name they cannot read answers nothing. Controls go BELOW the
                // label so both get the full width at any card size.
                <div
                  key={slot.value}
                  data-testid={`kyc-slot-${slot.value}`}
                  className="flex flex-col gap-2 p-3 rounded bg-obsidian border border-white/5"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">
                      {slot.label}
                      {slot.required
                        ? <span className="ml-2 text-[10px] uppercase tracking-wider text-red-400 font-semibold">required</span>
                        : <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-500">optional</span>}
                    </p>
                    {filled ? (
                      <p className="text-gray-500 text-xs truncate" title={slot.current.file_name} data-testid={`kyc-slot-file-${slot.value}`}>
                        {slot.current.file_name}
                      </p>
                    ) : (
                      <p className="text-gray-600 text-xs">Nothing uploaded yet</p>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    {filled && (
                      <Badge className={
                        slot.state === "approved" ? "bg-emerald/20 text-emerald" :
                        slot.state === "rejected" ? "bg-red-500/20 text-red-400" :
                        "bg-orange-500/20 text-orange-400"
                      }>
                        {slot.state}
                      </Badge>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={uploading}
                      onClick={() => triggerUpload(slot.value)}
                      data-testid={`kyc-slot-upload-${slot.value}`}
                      className="h-8 gap-1 border-white/10 text-gray-200 hover:bg-white/5"
                    >
                      <Upload size={12} />
                      {filled ? "Replace" : "Upload"}
                    </Button>
                  </div>
                </div>
              );
            })}
            <p className="text-gray-600 text-xs pt-1">PDF, JPG, PNG · max 10MB</p>
          </CardContent>
        </Card>

        {/* My Documents */}
        <Card className="bg-surface border-white/5 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white font-serif">My Documents</CardTitle>
            <CardDescription>Your uploaded verification documents</CardDescription>
          </CardHeader>
          <CardContent>
            {documents.length > 0 ? (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 rounded bg-obsidian border border-white/5">
                    <div className="flex items-center gap-3">
                      <FileCheck className="text-gold" size={20} />
                      <div>
                        <p className="text-white font-medium capitalize">{doc.document_type.replace(/_/g, " ")}</p>
                        <p className="text-gray-500 text-sm">{doc.file_name}</p>
                        <p className="text-gray-600 text-xs">Uploaded {formatDate(doc.uploaded_at)}</p>
                      </div>
                    </div>
                    <Badge className={
                      doc.status === "approved" ? "bg-emerald/20 text-emerald" :
                      doc.status === "rejected" ? "bg-red-500/20 text-red-400" :
                      "bg-orange-500/20 text-orange-400"
                    }>
                      {doc.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileCheck className="mx-auto mb-3 opacity-50" size={40} />
                <p>No documents uploaded yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Club Documents — bylaws download for approved members
          (STARSEED-CAPMARKETS-083) */}
      {canDownloadBylaws && (
        <Card className="bg-surface border-white/5 mt-6" data-testid="club-documents-card">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <ScrollText className="text-gold" size={20} />
              Club Documents
            </CardTitle>
            <CardDescription>Governance documents for approved members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4 p-4 rounded bg-obsidian border border-white/5 flex-wrap">
              <div>
                <p className="text-white font-medium">EchoSense Capital Club Bylaws</p>
                <p className="text-gray-500 text-sm mt-1">
                  Attorney-review draft — subject to change pending ratification of the
                  Membership Agreement, dues &amp; cancellation terms, and Privacy Notice.
                </p>
              </div>
              <Button
                onClick={downloadBylaws}
                disabled={downloadingBylaws}
                variant="outline"
                className="border-white/10 hover:bg-gold/10 hover:text-gold hover:border-gold/50 shrink-0"
                data-testid="download-bylaws-btn"
              >
                <Download size={16} className="mr-2" />
                {downloadingBylaws ? "Downloading…" : "Download Bylaws (PDF)"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: Pending Reviews */}
      {isAdmin && pendingDocs.length > 0 && (
        <Card className="bg-surface border-white/5 mt-6">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <Clock className="text-orange-400" size={20} />
              Pending KYC Reviews ({pendingDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 rounded bg-obsidian border border-white/5">
                  <div>
                    <p className="text-white font-medium capitalize">{doc.document_type.replace(/_/g, " ")}</p>
                    <p className="text-gray-500 text-sm">User: {doc.user_id}</p>
                    <p className="text-gray-600 text-xs">{formatDate(doc.uploaded_at)}</p>
                  </div>
                  <Dialog open={reviewDialog.open && reviewDialog.doc?.id === doc.id} onOpenChange={(open) => setReviewDialog({ open, doc: open ? doc : null })}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-white/10" data-testid={`review-${doc.id}`}>
                        <Eye size={16} className="mr-2" />
                        Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface border-white/10" aria-describedby="review-kyc-description">
                      <DialogHeader>
                        <DialogTitle className="text-white font-serif">Review KYC Document</DialogTitle>
                        <p id="review-kyc-description" className="sr-only">Form to review and approve or reject KYC documents</p>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="p-4 rounded bg-obsidian border border-white/5">
                          <p className="text-gray-400 text-sm">Document Type</p>
                          <p className="text-white capitalize">{doc.document_type.replace(/_/g, " ")}</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-300">Review Notes (Optional)</Label>
                          <Textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add notes about this document..."
                            className="bg-white border-slate-200 text-slate-900"
                          />
                        </div>
                        <div className="flex gap-3">
                          <Button 
                            onClick={() => handleReview(doc.id, "approved")}
                            className="flex-1 bg-emerald hover:bg-emerald-dark text-white"
                          >
                            <CheckCircle2 size={16} className="mr-2" />
                            Approve
                          </Button>
                          <Button 
                            onClick={() => handleReview(doc.id, "rejected")}
                            variant="destructive"
                            className="flex-1"
                          >
                            <XCircle size={16} className="mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default KYCPage;
